import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, Sparkles, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const LOGO_URL = "/pt-logo.png?v=2";

export default function CleaningResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tokenValid, setTokenValid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const token = searchParams.get('token');

  useEffect(() => {
    // Validate the token
    if (!token) {
      setTokenValid(false);
      setLoading(false);
      return;
    }

    // Decode JWT to check if valid and not expired
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        setTokenValid(false);
        setLoading(false);
        return;
      }
      
      const payload = JSON.parse(atob(parts[1]));
      const exp = payload.exp;
      const now = Math.floor(Date.now() / 1000);
      
      if (exp && exp < now) {
        setTokenValid(false);
        toast.error('This reset link has expired. Please request a new one.');
      } else if (payload.type === 'password_reset') {
        setTokenValid(true);
      } else {
        setTokenValid(false);
      }
    } catch (e) {
      setTokenValid(false);
    }
    setLoading(false);
  }, [token]);

  const handleReset = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setResetting(true);
    
    try {
      const response = await fetch(`${window.location.origin}/api/cleaning/admin/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setResetSuccess(true);
        toast.success('Password has been reset successfully!');
      } else {
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('Failed to reset password. Please try again.');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-500">Validating reset link...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="reset-password-invalid">
        <div className="w-full max-w-sm text-center">
          <img src={LOGO_URL} alt="Platinum Touch" className="h-16 w-auto mx-auto mb-6" />
          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 mb-2 font-['Outfit']">Invalid or Expired Link</h1>
            <p className="text-slate-500 text-sm mb-6">
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            <Link to="/cleaning/admin">
              <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (resetSuccess) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="reset-password-success">
        <div className="w-full max-w-sm text-center">
          <img src={LOGO_URL} alt="Platinum Touch" className="h-16 w-auto mx-auto mb-6" />
          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-500" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 mb-2 font-['Outfit']">Password Reset!</h1>
            <p className="text-slate-500 text-sm mb-6">
              Your password has been successfully reset. You can now log in with your new password.
            </p>
            <Link to="/cleaning/admin">
              <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="reset-password-form">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Platinum Touch" className="h-16 w-auto mx-auto mb-4" />
          <div className="flex items-center justify-center gap-2 mb-2">
            <Lock className="w-5 h-5 text-emerald-500" />
            <h1 className="text-xl font-bold text-slate-900 font-['Outfit']">Reset Password</h1>
          </div>
          <p className="text-slate-500 text-sm">Enter your new password below</p>
        </div>
        <form onSubmit={handleReset} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">New Password</label>
            <input 
              type="password" 
              value={newPassword} 
              onChange={e => setNewPassword(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-emerald-500 outline-none"
              placeholder="Enter new password" 
              required
              minLength={8}
              data-testid="new-password-input" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Confirm Password</label>
            <input 
              type="password" 
              value={confirmPassword} 
              onChange={e => setConfirmPassword(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-emerald-500 outline-none"
              placeholder="Confirm new password" 
              required 
              data-testid="confirm-password-input" 
            />
          </div>
          <Button 
            type="submit" 
            disabled={resetting} 
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-3" 
            data-testid="reset-password-btn"
          >
            <Lock className="w-4 h-4 mr-2" />
            {resetting ? 'Resetting...' : 'Reset Password'}
          </Button>
          <Link 
            to="/cleaning/admin"
            className="w-full flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-emerald-500 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
        </form>
      </div>
    </div>
  );
}
